import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import Button from '@/components/base/Button';
import EmptyState from '@/components/base/EmptyState';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import Toast, { type ToastType } from '@/components/base/Toast';
import ItemCard from '@/pages/items/components/ItemCard';
import { useAppData } from '@/store/AppDataContext';

interface ToastState {
  type: ToastType;
  message: string;
}

export default function ItemsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    items,
    executionTimes,
    saveExecutionTimes,
    patchItem,
    patchService,
    deleteItem,
  } = useAppData();

  const [execDraft, setExecDraft] = useState(executionTimes);
  const [execChanged, setExecChanged] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [toast, setToast] = useState<ToastState | null>(null);

  // رسالة قادمة من إحدى الصفحات المنفصلة بعد الحفظ
  useEffect(() => {
    const state = location.state as { toast?: ToastState } | null;
    if (state?.toast) {
      setToast(state.toast);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    setExecDraft(executionTimes);
  }, [executionTimes]);

  const showToast = (type: ToastType, message: string) => setToast({ type, message });

  const handleToggleActive = (id: string, active: boolean) => {
    patchItem(id, { active: !active });
    showToast('info', active ? 'تم تعطيل القطعة' : 'تم تفعيل القطعة');
  };

  const confirmDelete = () => {
    deleteItem(deleteId);
    showToast('info', 'تم حذف القطعة');
    setDeleteId('');
  };

  const saveExecTimes = () => {
    saveExecutionTimes(execDraft);
    setExecChanged(false);
    showToast('success', 'تم حفظ مدد التنفيذ');
  };

  return (
    <DashboardLayout title="القطع والخدمات">
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-bold text-[18px] text-foreground-950">القطع والخدمات</h2>
          <Button variant="cta" size="md" icon="ri-add-line" onClick={() => navigate('/items/new')}>
            أضف قطعة جديدة
          </Button>
        </div>

        <div className="rounded-card border border-background-200 bg-white p-5 shadow-card">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="font-semibold text-[14px] text-foreground-900">مدة التنفيذ</h3>
              <p className="mt-1 text-[12px] text-foreground-400">
                تنطبق على جميع الطلبات من لحظة استلام المغسلة للطلب
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-medium text-foreground-700">عادي:</span>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={1}
                    max={240}
                    value={execDraft.normal}
                    onChange={(event) => {
                      setExecDraft((prev) => ({ ...prev, normal: Number(event.target.value) }));
                      setExecChanged(true);
                    }}
                    className="h-10 w-16 rounded-field border border-background-300 px-3 text-center text-[14px] font-semibold text-foreground-950 focus:border-secondary-400 focus:outline-none"
                  />
                  <span className="text-[13px] text-foreground-500">ساعة</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-medium text-accent-700">مستعجل:</span>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={1}
                    max={48}
                    value={execDraft.urgent}
                    onChange={(event) => {
                      setExecDraft((prev) => ({ ...prev, urgent: Number(event.target.value) }));
                      setExecChanged(true);
                    }}
                    className="h-10 w-16 rounded-field border border-background-300 px-3 text-center text-[14px] font-semibold text-foreground-950 focus:border-secondary-400 focus:outline-none"
                  />
                  <span className="text-[13px] text-foreground-500">ساعة</span>
                </div>
              </div>
              {execChanged && (
                <Button variant="primary" size="sm" icon="ri-save-line" onClick={saveExecTimes}>
                  حفظ
                </Button>
              )}
            </div>
          </div>
          {!execChanged && (
            <div className="mt-3 flex items-center gap-3 border-t border-background-100 pt-3">
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-foreground-500">عادي:</span>
                <span className="font-semibold text-[13px] text-foreground-900">
                  {executionTimes.normal} ساعة
                </span>
              </div>
              <span className="text-foreground-300">•</span>
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-foreground-500">مستعجل:</span>
                <span className="font-semibold text-[13px] text-accent-700">
                  {executionTimes.urgent} ساعة
                </span>
              </div>
            </div>
          )}
        </div>

        {items.length === 0 ? (
          <EmptyState
            icon="ri-shirt-line"
            title="لم تضف أي قطع بعد"
            message="ابدأ بإضافة أول قطعة للمغسلة وحدد أسعارها الستة."
            action={
              <Button variant="cta" icon="ri-add-line" onClick={() => navigate('/items/new')}>
                أضف أول قطعة
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {items.map((item) => (
              <ItemCard
                key={item.id}
                item={item}
                onToggleActive={() => handleToggleActive(item.id, item.active)}
                onToggleService={(service, enabled) => patchService(item.id, service, { enabled })}
                onPriceChange={(service, tier, price) => patchService(item.id, service, { [tier]: price })}
                onEdit={() => navigate(`/items/${item.id}/edit`)}
                onDelete={() => setDeleteId(item.id)}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(deleteId)}
        title="تأكيد حذف القطعة"
        message="هل أنت متأكد من حذف هذه القطعة؟ سيتم إزالتها نهائياً من القائمة ولا يمكن التراجع."
        confirmLabel="حذف القطعة"
        tone="danger"
        onConfirm={confirmDelete}
        onClose={() => setDeleteId('')}
      />
    </DashboardLayout>
  );
}